import { FC } from 'react';
import {
  Modal,
  ModalControlModalProps,
  Text,
  useAlert,
  DropdownField,
  useFormField,
} from '@weave/design-system';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { SubscriptionAddOnEnums } from '@weave/schema-gen-ts/dist/schemas/subscription-manager-service/v1/subscription_manager_service.pb';
import { useQueryClient } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentLocation } from 'redux/actions/location';
import { getLocationFeatures } from 'redux/actions/customization/customization.action';
import {
  useMultiLocationTrialSignUp,
  TrialSubscriptionQueryKeys,
} from 'apis/trial-subscription/queries';
import { customizationFlagsQueryKeys } from 'components/customization-page-container/customization-page.queries';
import { FeatureEnumToNameMap } from 'constants/feature-data';
import { addDays, formatDate } from 'helpers/date';
import { RequestSource } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { css } from '@emotion/core';

export interface StartTrialModalProps extends ModalControlModalProps {
  featureEnum: Feature;
  addonEnum: SubscriptionAddOnEnums;
}

const TRIAL_DAYS_OPTIONS = [
  { label: '15 days', value: 15 },
  { label: '20 days', value: 20 },
  { label: '30 days', value: 30 },
];
const DEFAULT_TRIAL_DAYS = 30;

export const StartTrialModal: FC<StartTrialModalProps> = ({
  featureEnum,
  addonEnum,
  ...modalProps
}) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const locationId = useSelector(selectCurrentLocation)?.LocationID;

  const featureName = FeatureEnumToNameMap[featureEnum] ?? featureEnum;

  const trialDaysField = useFormField({
    type: 'dropdown',
    value: DEFAULT_TRIAL_DAYS.toString(),
  });

  const selectedDays = Number(trialDaysField.value) || DEFAULT_TRIAL_DAYS;
  const trialEndDate = formatDate(addDays(new Date(), selectedDays), 'MMMM Do, YYYY');

  const { mutate: signUp, isLoading } = useMultiLocationTrialSignUp({
    onSuccess: () => {
      queryClient.invalidateQueries(customizationFlagsQueryKeys.base);
      queryClient.invalidateQueries(TrialSubscriptionQueryKeys.baseQueryKey);
      if (locationId) {
        dispatch(getLocationFeatures(locationId));
      }
      modalProps.onClose();
    },
    onError: () => {
      alert.error('Failed to start trial. Please try again.');
    },
  });

  const handleBeginTrial = () => {
    if (locationId) {
      signUp({
        locationIds: [locationId],
        featureEnum,
        requestSource: RequestSource.REQUEST_SOURCE_WAM,
        trialDurationDays: selectedDays,
      });
    } else {
      alert.error('No location selected.');
    }
  };

  return (
    <Modal {...modalProps} maxWidth={480}>
      <Modal.Header onClose={modalProps.onClose}>Start Trial</Modal.Header>
      <Modal.Body
        css={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <div
          css={css`
            margin: 16px 0;
          `}
        >
          <DropdownField
            {...trialDaysField}
            name="trialDurationDays"
            label="Trial Duration (Days)"
          >
            {TRIAL_DAYS_OPTIONS.map((option) => (
              <DropdownField.Option key={option.value} value={option.value.toString()}>
                {option.label}
              </DropdownField.Option>
            ))}
          </DropdownField>
        </div>
        <Text>
          You are about to start a free trial for <strong>{featureName}</strong>. This
          trial will end on {trialEndDate}.
        </Text>
      </Modal.Body>
      <Modal.Actions
        primaryLabel="Begin Trial"
        onPrimaryClick={handleBeginTrial}
        disablePrimary={isLoading}
      />
    </Modal>
  );
};
